import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import projectAccessApi from '../../services/admin/projectAccessApi';

/**
 * プロジェクトアクセス管理機能のためのカスタムフック
 */
const useProjectAccess = () => {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userProjects, setUserProjects] = useState({});
  const [projectUsers, setProjectUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // 全ユーザーを取得
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectAccessApi.getAllUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message || '取得中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'ユーザー一覧の取得に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // 全プロジェクトを取得
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectAccessApi.getAllProjects();
      setProjects(data);
    } catch (err) {
      setError(err.message || '取得中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'プロジェクト一覧の取得に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // ユーザーのプロジェクト一覧を取得
  const fetchUserProjects = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectAccessApi.getUserProjects(userId);
      setUserProjects(prev => ({
        ...prev,
        [userId]: data
      }));
      return data;
    } catch (err) {
      setError(err.message || '取得中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'ユーザーのプロジェクト一覧の取得に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // プロジェクトのユーザー一覧を取得
  const fetchProjectUsers = useCallback(async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectAccessApi.getProjectUsers(projectId);
      setProjectUsers(prev => ({
        ...prev,
        [projectId]: data
      }));
      return data;
    } catch (err) {
      setError(err.message || '取得中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'プロジェクトのユーザー一覧の取得に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // プロジェクトアクセス権を付与
  const grantAccess = useCallback(async (userId, projectId) => {
    try {
      setLoading(true);
      setError(null);
      await projectAccessApi.grantAccess(userId, projectId);
      
      // ユーザーのプロジェクト一覧を更新
      if (userProjects[userId]) {
        const project = projects.find(p => p.id === projectId);
        if (project) {
          setUserProjects(prev => ({
            ...prev,
            [userId]: [...prev[userId], project]
          }));
        }
      }
      
      // プロジェクトのユーザー一覧を更新
      if (projectUsers[projectId]) {
        const user = users.find(u => u.email === userId);
        if (user) {
          setProjectUsers(prev => ({
            ...prev,
            [projectId]: [...prev[projectId], user]
          }));
        }
      }
      
      toast({
        title: 'アクセス権を付与しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return true;
    } catch (err) {
      setError(err.message || '処理中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'アクセス権の付与に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, userProjects, projectUsers, projects, users]);

  // プロジェクトアクセス権を削除
  const revokeAccess = useCallback(async (userId, projectId) => {
    try {
      setLoading(true);
      setError(null);
      await projectAccessApi.revokeAccess(userId, projectId);
      
      // ユーザーのプロジェクト一覧を更新
      if (userProjects[userId]) {
        setUserProjects(prev => ({
          ...prev,
          [userId]: prev[userId].filter(p => p.id !== projectId)
        }));
      }
      
      // プロジェクトのユーザー一覧を更新
      if (projectUsers[projectId]) {
        setProjectUsers(prev => ({
          ...prev,
          [projectId]: prev[projectId].filter(u => u.email !== userId)
        }));
      }
      
      toast({
        title: 'アクセス権を削除しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return true;
    } catch (err) {
      setError(err.message || '処理中にエラーが発生しました');
      toast({
        title: 'エラー',
        description: 'アクセス権の削除に失敗しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, userProjects, projectUsers]);

  // 初期読み込み
  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, [fetchUsers, fetchProjects]);

  return {
    users,
    projects,
    userProjects,
    projectUsers,
    loading,
    error,
    fetchUsers,
    fetchProjects,
    fetchUserProjects,
    fetchProjectUsers,
    grantAccess,
    revokeAccess
  };
};

export default useProjectAccess;